/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Box, Checkbox, FormLabel, Select, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { BoundToEdge, Components, PanelItem } from '@rta/rta-blueprint-library';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { lang } from '@lang';
import { useDesign } from '@hooks';
import { InlineNumberInput, InlineSlider, StackedStringInput } from './controls';
import { SkuSearchInput } from './sku-search-input';

type CustomAppliancePropertiesPanelProps = {
	id: string | null;
};

export const CustomAppliancePropertiesPanel: React.FC<CustomAppliancePropertiesPanelProps> = (props: CustomAppliancePropertiesPanelProps): JSX.Element => {
	const { id } = props;

	const colorSectionHeader = useColorModeValue('gray.600', 'gray.300');

	const [controller] = useDesign<BpController>();
	const kitchen = controller?.kitchen ?? null;
	const readonly = controller?.readonly ?? true;
	const components = kitchen?.context.components ?? [];

	const [gasChoice, setGasChoice] = useState<string>('');
	const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
	const [cutsIntoCountertop, setCutsIntoCountertop] = useState<boolean>(false);

	useEffect(() => {
		if (controller) {
			const locator = id ? kitchen?.getReferenceMap(id) ?? null : null;
			const pi: PanelItem | null = locator?.panelItem ?? null;

			setPanelItem(pi);
			if (pi) {
				const { placement, distanceBelowCountertop } = pi.item.component;
				setCutsIntoCountertop(placement === Components.PLACEMENT_TOP_ONLY && (distanceBelowCountertop ?? 0) > 0);
			}
		}
	}, [controller, id]);

	if (!controller || !panelItem || !kitchen) return <></>;

	const applyPlacement = (placement: string): void => {
		if (!panelItem.item.component) return;
		panelItem.item.component.placement = placement;

		const { cutout } = panelItem.item.component;

		if (placement === Components.PLACEMENT_FRONT_AND_TOP) {
			panelItem.item.component.distanceBelowCountertop = cutout.height;
		} else if (placement === Components.PLACEMENT_TOP_ONLY) {
			panelItem.item.component.distanceBelowCountertop = cutout.height;
			panelItem.item.component.distanceFrontOfFace = null;
			panelItem.center.z = 0;
		} else {
			panelItem.item.component.distanceBelowCountertop = 0;
		}

		// Sync vertical edge binding to the new placement (matches PanelItem constructor / withPlaceholderItem).
		// Without this, a FRONT_ONLY item switched from a Top-bound default keeps boundTop=true and stays
		// stuck to the top edge of the panel like a drop-in front cutout.
		const verticalBound = placement === Components.PLACEMENT_FRONT_AND_TOP || placement === Components.PLACEMENT_TOP_ONLY
			? BoundToEdge.Top
			: placement === Components.PLACEMENT_BOTTOM_ONLY
				? BoundToEdge.Bottom
				: BoundToEdge.No;
		// eslint-disable-next-line no-bitwise
		panelItem.boundToEdge = (panelItem.boundToEdge & (BoundToEdge.Left | BoundToEdge.Right)) | verticalBound;

		setCutsIntoCountertop(placement === Components.PLACEMENT_TOP_ONLY);

		if (placement !== Components.PLACEMENT_TOP_ONLY && panelItem.item.component.distanceFrontOfFace === null) {
			panelItem.item.component.distanceFrontOfFace =
				placement === Components.PLACEMENT_FRONT_AND_TOP
					? 0
					: null;
		}
	};

	const title = lang.contextPanels.title.customApplianceProperties;

	return <ContextPanelContent title={title}>
		<Stack width="100%" pt="10px" spacing={'5px'}>
			<StackedStringInput
				label="Sku"
				placeholder="Sku code..."
				value={panelItem.item.component?.customLabel ?? ''}
				enabled={!readonly}
				onChange={((e) => {
					if (panelItem.item.component) panelItem.item.component.customLabel = e.target.value;
					console.log('label', panelItem.item.component?.customLabel, panelItem.item.component?.sku);
				})}
			/>
			<StackedStringInput
				label="Description"
				placeholder="Appliance description..."
				value={panelItem.item.component?.description ?? ''}
				enabled={!readonly}
				onChange={((e) => {
					if (panelItem.item.component) panelItem.item.component.description = e.target.value;
				})}
			/>

			<Box>
				<FormLabel htmlFor="vendor" fontSize="small" marginBottom="0">Vendor:</FormLabel>
				<Select id="vendor" height="2em"
					fontSize="small"
					value={panelItem.item.component?.vendorId ?? ''}
					disabled={readonly}
					onChange={(e) => {
						if (panelItem.item.component) {
							panelItem.item.component.vendorId = e.target.value;
						}
					}}
				>
					<option value="">Other</option>
					{ kitchen.context.vendors
						.filter((v) => v.active)
						.map((v, i) => <option key={i} value={v.id}>{v.displayValue}</option>)}
				</Select>
			</Box>

			<Box>
				<FormLabel htmlFor="appliance-type" fontSize="small" marginBottom="0">Type:</FormLabel>
				<Select id="appliance-type" height="2em"
					fontSize="small"
					value={panelItem.item.component?.categoryId ?? ''}
					disabled={readonly}
					onChange={(e) => {
						if (panelItem.item.component) panelItem.item.component.categoryId = e.target.value;
						const { defaultPlacement } = kitchen.context.categories.findById(e.target.value) ?? {};
						if (defaultPlacement) applyPlacement(defaultPlacement);
					}}
				>
					{ kitchen.context.categories
						.filter((c) => c.active)
						.map((cat, i) => <option key={i} value={cat.id}>{cat.description}</option>)}
				</Select>
			</Box>

			<Box>
				<FormLabel htmlFor="appliance-placement" fontSize="small" marginBottom="0">Placement:</FormLabel>
				<Select id="appliance-placement" height="2em"
					fontSize="small"
					value={panelItem.item.component?.placement ?? ''}
					disabled={readonly}
					onChange={(e) => applyPlacement(e.target.value)}
				>
					{ Components.placement.map((p, i) => {
						return <option key={i} value={p.key}>{p.displayName}</option>;
					})}
				</Select>
			</Box>

			{ panelItem.item.component?.placement === Components.PLACEMENT_TOP_ONLY &&
				<Checkbox
					isChecked={cutsIntoCountertop}
					disabled={readonly}
					onChange={(e) => {
						const { checked } = e.target;
						setCutsIntoCountertop(checked);
						if (panelItem.item.component) {
							panelItem.item.component.distanceBelowCountertop = checked
								? panelItem.item.component.cutout.height
								: 0;
						}
					}}
				>
					<Text fontSize="small">Cuts Into Countertop</Text>
				</Checkbox>
			}

			<Box>
				<FormLabel htmlFor="appliance-gas-choice" fontSize="small" marginBottom="0">Fuel Options:</FormLabel>
				<Select id="appliance-gas-choice" height="2em"
					fontSize="small"
					value={gasChoice}
					onChange={(event) => setGasChoice(event.target.value)}
					disabled={readonly}
				>
					<option value="">Use Kitchen Default</option>
					<option value="CH">Charcoal</option>
					<option value="EL">Electric</option>
					<option value="PE">Pellet</option>
					<option value="LP">Propane</option>
					<option value="NG">Natural Gas</option>
				</Select>
			</Box>

			<Box marginBottom={3} padding={2} textAlign="left" borderLeft="1px solid grey">
				<Text paddingBottom={3} fontSize="medium">Dimensional Info.</Text>
				<Stack spacing="3" width="100%" height="65%">
					<InlineNumberInput
						category="cutout"
						label="Cutout Width"
						min={0.5}
						enabled={!readonly}
						value={panelItem.item.cutout.width}
						onChange={(e) => { panelItem.item.cutout = { width: e }; }}
					/>
					<InlineNumberInput
						category="cutout"
						label="Cutout Height"
						min={0.5}
						enabled={!readonly}
						value={panelItem.item.cutout.height}
						onChange={(e) => {
							panelItem.item.cutout = { height: e };
							const { placement, distanceBelowCountertop } = panelItem.item.component;
							const cutsIntoCountertop = placement === Components.PLACEMENT_TOP_ONLY && distanceBelowCountertop > 0;
							if (placement === Components.PLACEMENT_FRONT_AND_TOP || cutsIntoCountertop) {
								panelItem.item.component.distanceBelowCountertop = e;
							}
						}}
					/>
					<InlineNumberInput
						category="cutout"
						label="Cutout Depth"
						min={0}
						enabled={!readonly}
						value={panelItem.item.cutout.depth}
						onChange={(e) => { panelItem.item.cutout = { depth: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Overall Width" min={0.5}
						enabled={!readonly}
						value={panelItem.item.size.width}
						onChange={(e) => { panelItem.item.size = { width: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Overall Height" min={0.5}
						enabled={!readonly}
						value={panelItem.item.size.height}
						onChange={(e) => { panelItem.item.size = { height: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Overall Depth" min={0}
						enabled={!readonly}
						value={panelItem.item.component.size.depth}
						onChange={(e) => { panelItem.item.size = { depth: e }; }}
					/>
					<InlineNumberInput
						category="appliance" label="Distance Below Counter" min={0}
						value={ panelItem.item.component.distanceBelowCountertop ?? 0 }
						enabled={!readonly && (
							panelItem.item.component.placement === Components.PLACEMENT_FRONT_AND_TOP
							|| cutsIntoCountertop
						)}
						onChange={(e) => { panelItem.item.component.distanceBelowCountertop = e; }}
					/>
					<InlineNumberInput
						category="appliance" label="Distance Front Of Face" min={0}
						enabled={!readonly && panelItem.item.component.placement === Components.PLACEMENT_FRONT_AND_TOP}
						value={ panelItem.item.component.distanceFrontOfFace }
						onChange={(e) => {
							panelItem.item.component.distanceFrontOfFace = panelItem.item.component.placement === Components.PLACEMENT_FRONT_AND_TOP
								? e
								: null;
						}}
					/>
				</Stack>
			</Box>

			{ panelItem &&
				<Box>
					<Text pt="5px" pl="5px" color={colorSectionHeader} fontSize="medium"
						fontWeight="medium"
					>Dimensional Details</Text>
					<Box mb={3} padding={2} textAlign="left"
						borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
					>
						<Stack spacing="3" width="100%" height="65%">
							<InlineSlider
								category="dimension" label="H. Line"
								min={0} max={1} step={0.01}
								enabled={!readonly}
								value={ panelItem.dimensionOffsets.hLine }
								onChange={(e) => {
									panelItem.dimensionOffsets = { hLine: e };
								}}
							/>
							<InlineSlider
								category="dimension" label="H. Label"
								min={0} max={1} step={0.01}
								enabled={!readonly}
								value={ panelItem.dimensionOffsets.hLabel }
								onChange={(e) => {
									panelItem.dimensionOffsets = { ...panelItem.dimensionOffsets, hLabel: e };
								}}
							/>
							<InlineSlider
								category="dimension" label="V. Line"
								min={0} max={1} step={0.01}
								enabled={!readonly}
								value={ panelItem.dimensionOffsets.vLine }
								onChange={(e) => {
									panelItem.dimensionOffsets = { ...panelItem.dimensionOffsets, vLine: e };
								}}
							/>
							<InlineSlider
								category="dimension" label="V. Label"
								min={0} max={1} step={0.01}
								enabled={!readonly}
								value={ panelItem.dimensionOffsets.vLabel }
								onChange={(e) => {
									panelItem.dimensionOffsets = { vLabel: e };
								}}
							/>
						</Stack>
					</Box>
				</Box>
			}
			<Box>
				<SkuSearchInput
					components={[...components]}
					value={panelItem.item.component.placeholderModelSku}
					excludeSku={panelItem.item.component.sku}
					onChange={(sku) => { panelItem.item.component.placeholderModelSku = sku; }}
					enabled={!readonly}
				/>
			</Box>
		</Stack>
	</ContextPanelContent>;
};
